import { useMemo, useState } from "preact/hooks";

import { api } from "@/api/client";
import type { PreviewToken } from "@/api/types";
import { CopyButton } from "@/components/CopyButton";
import { PanelHelp } from "@/components/PanelHelp";
import { useDebounced } from "@/hooks/useDebounced";
import { type PollResult, usePoll } from "@/hooks/usePoll";
import { t, type TranslationKey } from "@/i18n";
import {
  DEFAULT_EMBED,
  EMBED_LANGS,
  EMBED_MODES,
  type EmbedMode,
  type EmbedSettings,
  type EmbedVariant,
  embedSnippet,
  previewDocument,
  WIDGET_BASE,
} from "@/lib/embedSnippet";
import { formatDateTime, modeLabel } from "@/lib/format";
import { runSummary } from "@/lib/reasoningDepth";
import { errorDetail } from "@/lib/runtimeChange";
import { currentView } from "@/state/filters";

// «Betrieb › Testseite»: an embed put together from every `data-*` setting,
// previewed with the real widget and backend, and the code to copy onto a
// partner page; the search bar also gets a guide for a demo. The preview's
// sessions are test sessions (a preview token the backend mints), so they stay
// out of statistics and costs and get the models switched for test sessions
// only.

const VARIANTS: readonly EmbedVariant[] = ["bubble", "inline", "search"];

const PRESETS: readonly { label: TranslationKey; settings: Partial<EmbedSettings> }[] = [
  {
    label: "testpage.preset.gateway",
    settings: { variant: "bubble", mode: "faq", modes: ["faq", "discovery", "trainer"] },
  },
  { label: "testpage.preset.profession", settings: { variant: "inline", mode: "profession" } },
  {
    label: "testpage.preset.partner",
    settings: { variant: "bubble", mode: "faq", primaryColor: "#1e7a46", brandName: "Partner" },
  },
  { label: "testpage.preset.search", settings: { variant: "search", mode: "faq" } },
];

// The search bar's guide: what a visitor sees, then how a page gets the bar.
const SEARCH_FLOW: readonly TranslationKey[] = [
  "testpage.guide.flow.examples",
  "testpage.guide.flow.dialog",
  "testpage.guide.flow.fresh",
  "testpage.guide.flow.private",
];
const SEARCH_STEPS: readonly TranslationKey[] = [
  "testpage.guide.how.allow",
  "testpage.guide.how.paste",
  "testpage.guide.how.options",
  "testpage.guide.how.test",
];

// A token lives eight hours; a fresh one well before that.
const TOKEN_REFRESH_MS = 4 * 60 * 60_000;
// Typing in a field rebuilds the preview once the typing stops.
const PREVIEW_DELAY_MS = 600;

const DEVICE_WIDTH = { desktop: "100%", mobile: "390px" } as const;
type Device = keyof typeof DEVICE_WIDTH;

interface Profession {
  id: string;
  name: string;
}

const isProfession = (value: unknown): value is Profession =>
  typeof (value as Profession | null)?.id === "string" &&
  typeof (value as Profession | null)?.name === "string";

/** The widget's own profession catalogue, the one its picker offers. */
async function fetchProfessions(signal: AbortSignal): Promise<Profession[]> {
  const response = await fetch(`${WIDGET_BASE}/widget/professions.json`, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const list: unknown = await response.json();
  return Array.isArray(list) ? list.filter(isProfession) : [];
}

const asMode = (value: string): EmbedMode => EMBED_MODES.find((m) => m === value) ?? "faq";
const lines = (text: string): string[] => text.split("\n");

/** What the preview's sessions are, in words. */
function tokenStatus(token: PollResult<PreviewToken>): { ok: boolean; text: string } | null {
  if (token.data) {
    return {
      ok: true,
      text: t("testpage.token.on", { until: formatDateTime(token.data.expires_at) }),
    };
  }
  if (!token.error) return null;
  if (errorDetail(token.error).error === "preview_token_disabled") {
    return { ok: false, text: t("testpage.token.off") };
  }
  return { ok: false, text: t("testpage.token.error", { error: token.error.message }) };
}

export function TestPageView() {
  const runtime = usePoll((s) => api.runtime(s), [], { intervalMs: 60_000 });
  const token = usePoll((s) => api.previewToken(s), [], { intervalMs: TOKEN_REFRESH_MS });
  const professions = usePoll(fetchProfessions, [], { intervalMs: 24 * 60 * 60_000 });

  const [settings, setSettings] = useState<EmbedSettings>(DEFAULT_EMBED);
  const [device, setDevice] = useState<Device>("desktop");
  const [reloads, setReloads] = useState(0);
  const update = (patch: Partial<EmbedSettings>) => setSettings({ ...settings, ...patch });

  const snippet = embedSnippet(settings);

  const shown = useDebounced(settings, PREVIEW_DELAY_MS);
  const tokenSettled = token.data !== undefined || token.error !== undefined;
  const title = t("testpage.preview.title");
  const intro = t("testpage.preview.intro");
  // Rebuilt on a change and once the first token is in; a refreshed token
  // waits for the next rebuild, so the running preview keeps its conversation.
  // «Neu laden» remounts the frame (its key).
  const srcdoc = useMemo(
    () => previewDocument(shown, { token: token.data?.token ?? "", title, intro }),
    [shown, tokenSettled, title, intro],
  );

  const status = tokenStatus(token);
  const row = runtime.data?.modes.find((m) => m.mode === settings.mode);

  return (
    <div style="display: grid; gap: 24px;">
      <section class="card">
        <h2>{t("testpage.embed")}</h2>
        <PanelHelp k="testpage.embed" />
        <div class="toolbar testpage-presets">
          <span>{t("testpage.presets")}</span>
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              class="window-pill"
              onClick={() => setSettings({ ...DEFAULT_EMBED, ...preset.settings })}
            >
              {t(preset.label)}
            </button>
          ))}
        </div>

        <fieldset class="testpage-variants">
          <legend>{t("testpage.variant")}</legend>
          {VARIANTS.map((variant) => (
            <label key={variant} class="toggle">
              <input
                type="radio"
                name="testpage-variant"
                checked={settings.variant === variant}
                onInput={() => update({ variant })}
              />
              <span>{t(`testpage.variant.${variant}`)}</span>
            </label>
          ))}
        </fieldset>

        <div class="testpage-fields">
          <label class="testpage-field">
            <span>{t("testpage.mode")}</span>
            <select
              value={settings.mode}
              onInput={(e) => update({ mode: asMode((e.target as HTMLSelectElement).value) })}
            >
              {EMBED_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {modeLabel(mode)}
                </option>
              ))}
            </select>
          </label>

          {settings.mode === "profession" &&
            (professions.data?.length ? (
              <label class="testpage-field">
                <span>{t("testpage.profession")}</span>
                <select
                  value={settings.professionId}
                  onInput={(e) => {
                    const id = (e.target as HTMLSelectElement).value;
                    const name = professions.data?.find((p) => p.id === id)?.name ?? "";
                    update({ professionId: id, professionName: name });
                  }}
                >
                  <option value="">{t("testpage.profession.none")}</option>
                  {professions.data.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <>
                <TextField
                  label={t("testpage.profession.id")}
                  value={settings.professionId}
                  onValue={(professionId) => update({ professionId })}
                />
                <TextField
                  label={t("testpage.profession.name")}
                  value={settings.professionName}
                  onValue={(professionName) => update({ professionName })}
                />
              </>
            ))}

          <fieldset class="testpage-field">
            <legend>{t("testpage.menu")}</legend>
            {EMBED_MODES.map((mode) => (
              <label key={mode} class="toggle">
                <input
                  type="checkbox"
                  checked={mode === settings.mode || settings.modes.includes(mode)}
                  disabled={mode === settings.mode}
                  onInput={(e) => {
                    const on = (e.target as HTMLInputElement).checked;
                    const modes = settings.modes.filter((m) => m !== mode);
                    update({ modes: on ? [...modes, mode] : modes });
                  }}
                />
                <span>{modeLabel(mode)}</span>
              </label>
            ))}
          </fieldset>

          <label class="testpage-field">
            <span>{t("testpage.lang")}</span>
            <select
              value={settings.lang}
              onInput={(e) => update({ lang: (e.target as HTMLSelectElement).value })}
            >
              {EMBED_LANGS.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </label>

          <label class="testpage-field">
            <span>{t("testpage.theme")}</span>
            <select
              value={settings.theme}
              onInput={(e) => {
                const theme = (e.target as HTMLSelectElement).value;
                update({ theme: theme === "light" || theme === "dark" ? theme : "auto" });
              }}
            >
              <option value="auto">{t("testpage.theme.auto")}</option>
              <option value="light">{t("testpage.theme.light")}</option>
              <option value="dark">{t("testpage.theme.dark")}</option>
            </select>
          </label>

          {settings.variant === "bubble" && (
            <label class="testpage-field">
              <span>{t("testpage.position")}</span>
              <select
                value={settings.position}
                onInput={(e) =>
                  update({
                    position:
                      (e.target as HTMLSelectElement).value === "bottom-left"
                        ? "bottom-left"
                        : "bottom-right",
                  })
                }
              >
                <option value="bottom-right">{t("testpage.position.right")}</option>
                <option value="bottom-left">{t("testpage.position.left")}</option>
              </select>
            </label>
          )}

          <label class="testpage-field">
            <span>{t("testpage.color")}</span>
            <input
              type="color"
              value={settings.primaryColor}
              onInput={(e) => update({ primaryColor: (e.target as HTMLInputElement).value })}
            />
          </label>

          <TextField
            label={t("testpage.brand")}
            value={settings.brandName}
            onValue={(brandName) => update({ brandName })}
          />
          <TextField
            label={t("testpage.logo")}
            value={settings.logoUrl}
            onValue={(logoUrl) => update({ logoUrl })}
          />
          <TextArea
            label={t("testpage.greeting")}
            value={settings.greeting}
            onValue={(greeting) => update({ greeting })}
          />
          <TextArea
            label={t("testpage.starters")}
            value={settings.starters.join("\n")}
            onValue={(text) => update({ starters: lines(text) })}
          />
          {settings.variant === "search" ? (
            <>
              <TextArea
                label={t("testpage.examples")}
                value={settings.examples.join("\n")}
                onValue={(text) => update({ examples: lines(text) })}
              />
              <TextField
                label={t("testpage.placeholder")}
                value={settings.placeholder}
                onValue={(placeholder) => update({ placeholder })}
              />
            </>
          ) : (
            <TextField label={t("testpage.q")} value={settings.q} onValue={(q) => update({ q })} />
          )}
        </div>
      </section>

      {settings.variant === "search" && <SearchGuide />}

      <section class="card">
        <h2>{t("testpage.preview")}</h2>
        <PanelHelp k="testpage.preview" />
        <div class="toolbar">
          {(Object.keys(DEVICE_WIDTH) as Device[]).map((d) => (
            <button
              key={d}
              type="button"
              class={`window-pill ${device === d ? "active" : ""}`}
              aria-pressed={device === d}
              onClick={() => setDevice(d)}
            >
              {t(`testpage.preview.${d}`)}
            </button>
          ))}
          <button type="button" class="window-pill" onClick={() => setReloads(reloads + 1)}>
            {t("testpage.preview.reload")}
          </button>
        </div>
        {status && (
          <div class={status.ok ? "kpi-sub" : "hl-orange"} role="status">
            {status.text}
          </div>
        )}
        {tokenSettled && (
          <iframe
            key={reloads}
            class="testpage-frame"
            title={t("testpage.preview.frame")}
            srcdoc={srcdoc}
            style={{ width: DEVICE_WIDTH[device] }}
          />
        )}
      </section>

      <div class="grid grid-3">
        <section class="card">
          <h2>{t("testpage.code")}</h2>
          <PanelHelp k="testpage.code" />
          <pre class="testpage-code">
            <code data-testid="embed-code">{snippet}</code>
          </pre>
          <CopyButton
            text={snippet}
            label={t("testpage.copy")}
            done={t("testpage.copied")}
            failed={t("testpage.copyFailed")}
          />
        </section>

        <section class="card">
          <h2>{t("testpage.model")}</h2>
          <PanelHelp k="testpage.model" />
          {row && (
            <>
              <div>{t("testpage.model.test", { run: runSummary(row.test_sessions) })}</div>
              <div class="kpi-sub">{t("testpage.model.all", { run: runSummary(row.effective) })}</div>
            </>
          )}
          <button type="button" class="window-pill" onClick={() => (currentView.value = "ops")}>
            {t("testpage.model.switch")}
          </button>
        </section>
      </div>
    </div>
  );
}

/** What search.js does, what a visitor sees and how a page gets it, beside its demo. */
function SearchGuide() {
  return (
    <section class="card" aria-labelledby="testpage-guide-title">
      <h2 id="testpage-guide-title">{t("testpage.guide.title")}</h2>
      <p class="panel-help">{t("testpage.guide.what")}</p>
      <div class="testpage-guide">
        <div>
          <h3>{t("testpage.guide.flow")}</h3>
          <ol>
            {SEARCH_FLOW.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ol>
        </div>
        <div>
          <h3>{t("testpage.guide.how")}</h3>
          <ol>
            {SEARCH_STEPS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function TextField({
  label,
  value,
  onValue,
}: {
  label: string;
  value: string;
  onValue: (value: string) => void;
}) {
  return (
    <label class="testpage-field">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        onInput={(e) => onValue((e.target as HTMLInputElement).value)}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onValue,
}: {
  label: string;
  value: string;
  onValue: (value: string) => void;
}) {
  return (
    <label class="testpage-field">
      <span>{label}</span>
      <textarea
        rows={3}
        value={value}
        onInput={(e) => onValue((e.target as HTMLTextAreaElement).value)}
      />
    </label>
  );
}
